import axios from "axios";
import useStore from "../Store/Store";
import {
  openFile,
  doesFileExist,
} from "./LocalFileController/LocalFileController";
import { getFileDownloadURL } from "../Api/ServerInterface";
import { logger } from "./Logger";

/**
 * Utility to centralise handling of the .codecanvasignore rules so that the
 * same logic can be shared by multiple modules (search indexing, etc).
 */

// Fallback patterns – identical to the previous hard-coded list that lived in
// FlexSearchIndexer.ts. They are only used if a .codecanvasignore file cannot
// be located (e.g. legacy projects).
const FALLBACK_PATTERNS: string[] = [
  // Common directories / files
  "*node_modules*",
  "/dist/",
  "/build/",
  "/public/assets/",
  "/public/assets/images/",
  "/public/assets/images/icons/",
  "package-lock.json",
  "package.json",
  // Drawio specific
  "/drawio/src/main/webapp/node_modules/",
  "/drawio/src/main/webapp/assets/",
  "/drawio/src/main/webapp/resources/",
  "/drawio/src/main/webapp/shapes/",
  "/drawio/src/main/webapp/math/",
  "/drawio/src/main/webapp/plugins/",
  "/drawio/src/main/webapp/templates/",
  "/drawio/src/main/webapp/stencils/",
  "/drawio/src/main/webapp/WEB-INF/",
  "/drawio/src/main/webapp/images/",
  // Server & E2E
  "/server/node_modules/",
  "/server/dist/",
  "/server/build/",
  "/server/public/assets/",
  "/serverless-express/",
  "/E2ETests/node_modules/",
  "/E2ETests/dist/",
  "/E2ETests/build/",
  "/E2ETests/public/assets/",
  // Big individual files
  "mxClient.js",
  "/drawio/src/main/webapp/js/elk.bundled.js",
  "/drawio/src/main/webapp/mxgraph/mxClient.js",
  "/drawio/src/main/webapp/js/diagramly/vsdx/importer.js",
  "/drawio/src/main/webapp/js/diagramly/Extensions.js",
  "/drawio/src/main/webapp/js/stencils.min.js",
  "/drawio/src/main/webapp/js/app.min.js",
  "/drawio/src/main/webapp/js/viewer-static.min.js",
  "/drawio/src/main/webapp/js/extensions.min.js",
  "/drawio/src/main/webapp/js/viewer.min.js",
  "/drawio/src/main/webapp/js/shapes.min.js",
  "/drawio/src/main/webapp/js/shapes-14-6-5.min.js",
  "/drawio/src/main/webapp/js/mermaid/mermaid.min.js",
  "/drawio/src/main/webapp/js/orgchart.min.js",
  "/drawio/src/main/webapp/js/orgchart/bridge.min.js",
  // Extensions
  "*.min.js",
  "*.min.css",
  "*.map",
  "*.xml",
  "*.png",
  "*.jpg",
  "*.jpeg",
  "*.gif",
  "*.ico",
  "*.ttf",
  "*.woff",
  "*.woff2",
  "*.eot",
  "*.otf",
  "*.svg",
];

let ignorePatterns: string[] | null = null;

const parsePatterns = (raw: string): string[] => {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"));
};

/**
 * Loads the .codecanvasignore patterns for the currently opened repository.
 * If the file cannot be located the hard-coded fallback list is used.
 */
export const initializeIgnorePatterns = async () => {
  if (ignorePatterns !== null) {
    return; // already initialised
  }

  const sessionMode = useStore.getState().session.mode;
  try {
    if (sessionMode === "local") {
      const ignoreFilePath = ".codecanvasignore";
      const exists = await doesFileExist(ignoreFilePath);
      if (exists) {
        const openResult: any =
          (await openFile(ignoreFilePath, null, false)) || {};
        const fileContent = openResult.fileContent;
        if (fileContent) {
          ignorePatterns = parsePatterns(fileContent);
        } else {
          logger.info(
            `${ignoreFilePath} file found but empty. Using fallback patterns.`
          );
        }
      } else {
        logger.info(
          `${ignoreFilePath} file not found. Using fallback patterns.`
        );
      }
    } else {
      // github / unauthenticatedGithub flow – look for the file in repoData
      const repoData = useStore.getState().repoData as Record<string, any>;
      let ignoreFileMeta: any = null;
      for (const [key, value] of Object.entries(repoData || {})) {
        if (key.toLowerCase().endsWith(".codecanvasignore")) {
          ignoreFileMeta = value;
          break;
        }
      }
      if (ignoreFileMeta && ignoreFileMeta.url) {
        const currentBranch = useStore.getState().currentBranch;
        const resp = await getFileDownloadURL(
          ignoreFileMeta.url,
          ignoreFileMeta.path,
          currentBranch
        );
        const downloadUrl =
          resp.download_url || resp.download_url_with_branch || "";
        if (downloadUrl) {
          const { data } = await axios.get<string>(downloadUrl);
          ignorePatterns = parsePatterns(data);
        }
      }
    }
  } catch (e) {
    // Ignore & fall back – this is non-critical
    console.warn(
      "[CodeCanvasIgnore] Unable to load .codecanvasignore – falling back to defaults.",
      e
    );
  }

  if (!ignorePatterns || ignorePatterns.length === 0) {
    ignorePatterns = [...FALLBACK_PATTERNS];
  }

  // Persist the patterns in the global Zustand store so that other modules
  // can access them synchronously without having to re-initialise.
  try {
    if (Array.isArray(ignorePatterns)) {
      useStore.getState().setIgnorePatterns(ignorePatterns);
    }
  } catch (e) {
    console.warn(
      "[CodeCanvasIgnore] Unable to persist ignore patterns to global store",
      e
    );
  }
};

/**
 * Synchronous helper used by hot-paths (e.g. search indexing). Assumes that
 * `initializeIgnorePatterns()` has been awaited once during start-up of the
 * consuming task. If it hasn't, we fall back to the default list so we never
 * block on async from inside tight loops.
 */
export const isIgnoredPath = (filePath: string): boolean => {
  if (!ignorePatterns) {
    // Try to retrieve from global store first – this is the preferred source
    // once the patterns have been initialised during repository load.
    const patternsFromStore = useStore.getState()?.ignorePatterns;
    if (Array.isArray(patternsFromStore) && patternsFromStore.length > 0) {
      ignorePatterns = patternsFromStore;
    } else {
      // As a last-resort, fall back to the baked-in defaults so that we do
      // not unexpectedly index very large binary files.
      ignorePatterns = [...FALLBACK_PATTERNS];
    }
  }

  const lowerCasePath = filePath.toLowerCase();

  for (const pattern of ignorePatterns) {
    // Extension pattern (e.g. *.png or .png)
    if (pattern.startsWith("*.") || pattern.startsWith(".")) {
      const ext = pattern.replace(/^\*?\./, "").toLowerCase();
      if (lowerCasePath.endsWith(`.${ext}`)) {
        return true;
      }
      continue;
    }

    // Directory / substring pattern
    if (lowerCasePath.includes(pattern.toLowerCase())) {
      return true;
    }
  }
  return false;
};
